
import Vue from 'vue';
import {
  QuestionModel,
  AssertionModel,
  ReplyModel,
  UserModel,
  ListModel,
  ClassroomModel,
  CourseModel,
} from '@descomplica/dex-models';
import RevisionQuestion from '../components/RevisionQuestion.vue';
import RevisionNavbar from '../components/RevisionNavbar.vue';
import { arrayContainsArray } from '@/helpers/array';
import { Button, Icon } from '@descomplica/vue-components';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { BaseTrackType } from '@/helpers/analytics/types';
import { ANALYTICS_LABELS } from '@/helpers/listItems';
import { getCourseCategory } from '@/helpers/category';
import { UndergradGradesService, UserListActivityStatus } from '@/services/UndergradGrades';
import SentryClient from '@/helpers/sentry';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';

interface Data {
  currentQuestionIndex: number;
  userReplies: Map<number, ReplyModel>;
  currentReply: ReplyModel | null;
  revision: ListModel | null;
  listStatus: UserListActivityStatus | null;
}

export default Vue.extend({
  components: {
    RevisionQuestion,
    Button,
    Icon,
    RevisionNavbar,
  },
  data(): Data {
    return {
      currentQuestionIndex: 0,
      userReplies: new Map(),
      currentReply: null,
      revision: null,
      listStatus: null,
    };
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    title(): string | null {
      return this.revision && this.revision.name;
    },
    questions(): QuestionModel[] {
      return this.revision ? this.revision.questions : [];
    },
    currentQuestion(): QuestionModel {
      return this.questions[this.currentQuestionIndex];
    },
    isCurrentQuestionAnswered(): Boolean {
      return !!this.currentReply;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    course(): CourseModel {
      return this.user.getCourse();
    },
    currentClassroom(): ClassroomModel {
      return this.$store.getters.classroomBySlug(this.$route.params.classroomSlug);
    },
    classroomSlug(): String {
      return this.$router && this.$route.params.classroomSlug;
    },
    defaultTrackingProps(): BaseTrackType {
      return {
        course: this.course.name,
        discipline: this.currentClassroom.name,
        content_type: ANALYTICS_LABELS.revisionList,
        category: getCourseCategory(this.course),
      };
    },
    showDueDateWarning(): boolean {
      return this.$store.getters.isUndergraduateCourse && this.$store.getters.showDueDateWarning;
    },
  },
  async mounted() {
    await this.$store.dispatch('PULL_REVISION_LIST_ITEMS', {
      listSlug: this.$route && this.$route.params.revisionSlug,
    });
    this.revision = this.$store.getters.listBySlug(this.$route && this.$route.params.revisionSlug);
    this.updateCurrentReply();

    this.trackingContentOpened();

    if (this.showDueDateWarning) {
      await this.loadReviewListStatus();
    }
  },
  methods: {
    trackingContentOpened(): void {
      Analytics.trackEvent(EVENT_LABELS.contentOpened, {
        ...this.defaultTrackingProps,
        revision_name: this.revision?.name,
      });
    },
    trackingContentConsumed(assertionCorrect: boolean, questionId: number): void {
      if (!questionId) return;

      Analytics.trackEvent(EVENT_LABELS.contentConsumed, {
        ...this.defaultTrackingProps,
        list_review_name: this.title,
        content_id: questionId,
        content_status: assertionCorrect ? 'right' : 'wrong',
        ...this.getTrialStatusByDescomplicaCourseId,
      });
    },
    goToNext(): void {
      if (this.hasAllQuestionsDone()) {
        this.goToResult();
      } else if (
        this.currentQuestionIndex + 1 ===
        (this.revision && this.revision.questions.length)
      ) {
        this.goToFirstUnansweredQuestion();
      } else {
        this.goToQuestion(this.currentQuestionIndex + 1);
      }
    },
    goToResult(): void {
      this.$router.push({
        name: 'revisionResult',
        params: {
          classroomSlug: this.$route && this.$route.params.classroomSlug,
          revisionSlug: this.$route && this.$route.params.revisionSlug,
        },
      });
    },
    goToQuestion(index: number): void {
      this.currentQuestionIndex = index;
      this.updateCurrentReply();
    },
    updateCurrentReply(): void {
      const reply = this.userReplies.get(this.currentQuestion.id);
      this.currentReply = reply || null;
    },
    goToFirstUnansweredQuestion(): void {
      const userReplyQuestionIdList = [...this.userReplies.keys()];
      const questionIdList = this.questions.map((question) => question.id);
      const unansweredQuestionIdList = questionIdList.filter(
        (question) => !userReplyQuestionIdList.includes(question),
      );

      const indexOfUnansweredQuestion = questionIdList.indexOf(unansweredQuestionIdList[0]);
      this.goToQuestion(indexOfUnansweredQuestion);
    },
    hasAllQuestionsDone(): Boolean {
      const userReplyQuestionIdList = [...this.userReplies.keys()];
      const questionIdList = this.questions.map((question) => question.id);
      return arrayContainsArray(userReplyQuestionIdList, questionIdList);
    },
    addNewReply(data: {
      question: QuestionModel;
      assertionId: number;
      assertionCorrect: boolean;
    }): void {
      const { question, assertionId } = data;

      const foundAssertion: AssertionModel | undefined = question.assertions.find(
        (assertion: AssertionModel) => assertion.id === assertionId,
      );

      if (!foundAssertion || !foundAssertion.id || !this.revision) {
        return;
      }

      this.$store.dispatch('CREATE_REPLY', {
        listId: this.revision.id,
        questionId: question.id,
        userId: this.user.id,
        assertionId,
        answer: foundAssertion.position,
      });

      const reply = new ReplyModel(
        {
          id: 1,
          questionId: question.id,
          assertionId,
        },
        {},
      );

      this.trackingContentConsumed(foundAssertion.correct, question.id);
      this.userReplies.set(question.id, reply);
      this.updateCurrentReply();
    },
    async loadReviewListStatus() {
      try {
        if (!this.revision?.id) return;

        const listStatusResponse = await new UndergradGradesService().getListStatus(
          this.currentClassroom.id,
          this.revision?.id,
        );

        this.listStatus = listStatusResponse?.reviewLists ?? null;
      } catch (error) {
        SentryClient.captureError(error);
      }
    },
  },
});
